import { useCallback } from 'react';

import DataTypeInput, {
  BooleanOrientation,
} from '@atom/components/common/dataTypeInput/DataTypeInput';
import { Icon, Tooltip } from '@atom/mui';
import colors from '@atom/styles/colors';
import { SchemaAttribute } from '@atom/types/schema';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from '../chat.module.css';

type ChatAssetDraftAttributeRowProps = {
  attribute: SchemaAttribute;
  value: any;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
  onChange: (id: string, value: any) => void;
  error: boolean;
  disabled?: boolean;
};

const CONFIDENCE_CONFIG: Record<string, { color: string; label: string }> = {
  medium: { color: colors.brand.orange, label: 'Medium confidence' },
  low: { color: colors.brand.red, label: 'Low confidence' },
};

const ChatAssetDraftAttributeRow = ({
  attribute,
  value,
  confidence,
  reason,
  onChange,
  error,
  disabled,
}: ChatAssetDraftAttributeRowProps) => {
  const handleChange = useCallback(
    (newValue: any) => {
      if (attribute?.id) {
        onChange(attribute.id, newValue);
      }
    },
    [attribute.id, onChange],
  );

  const name = attribute.isRequired ? `* ${attribute.name}` : attribute.name;
  const hasDefault = !isNilOrEmpty(attribute.defaultValue);
  const displayValue = hasDefault ? attribute.defaultValue : value;
  const isDisabled = disabled || !attribute.isEditable || hasDefault;
  const config = CONFIDENCE_CONFIG[confidence];

  const nameStyle = {
    color: error ? colors.brand.red : colors.neutral.dark,
  };

  return (
    <div className={classes.assetDraftCardAttributeRow}>
      <div className={classes.assetDraftCardAttributeName} style={nameStyle}>
        {name}
        {config && !disabled && (
          <Tooltip title={`${config.label}: ${reason}`} placement="top">
            <span className={classes.assetDraftCardConfidenceWarning}>
              <Icon style={{ fontSize: '0.75rem' }} color={config.color}>
                warning
              </Icon>
            </span>
          </Tooltip>
        )}
      </div>
      <div className={classes.assetDraftCardAttributeInput}>
        <DataTypeInput
          id={attribute.id ?? ''}
          dataType={attribute.dataType}
          options={attribute.enumeration}
          thousandsSeparator={attribute.thousandsSeparator}
          onChange={handleChange}
          value={displayValue}
          disabled={isDisabled}
          error={error}
          booleanOrientation={BooleanOrientation.HORIZONTAL}
          unit={attribute?.unit}
        />
      </div>
    </div>
  );
};

export default ChatAssetDraftAttributeRow;
